import { MealInsightControllerService } from "@9amhealth/openapi";
import {
  getLocalTimeZone,
  today as todayInZone
} from "@internationalized/date";
import { Cubit } from "blac-next";
import { globalEvents } from "src/constants/globalEvents";
import reportErrorSentry from "src/lib/reportErrorSentry";

export interface MacroStat {
  total: number;
  floor: number;
}

export interface CalorieStat {
  total: number;
  target: number;
}

export interface MealsStatsState {
  loading: boolean;
  loaded: boolean;
  insights: string;
  protein: MacroStat;
  fiber: MacroStat;
  calories: CalorieStat;
  error?: Error;
}

/**
 * Fraction (0..1) of a macro's daily floor/target that has been consumed, used
 * to fill the ring and the macro progress bars. `current` is today's consumed
 * amount, `floor` the daily floor (or, for calories, the target). Returns 0 when
 * the floor is missing/non-positive to avoid dividing by zero, and clamps to
 * [0, 1] so an over-target day fills exactly once rather than overflowing.
 */
export const macroFillFraction = (current: number, floor: number): number => {
  if (floor <= 0) {
    return 0;
  }
  return Math.min(Math.max(current / floor, 0), 1);
};

export const calorieTargetExceeded = (total: number, target: number): boolean =>
  target > 0 && total > target;

export const showsProteinGoal = (total: number, floor: number): boolean =>
  total === 0 && floor > 0;

const buildInitialState = (): MealsStatsState => ({
  loading: false,
  loaded: false,
  insights: "",
  protein: { total: 0, floor: 0 },
  fiber: { total: 0, floor: 0 },
  calories: { total: 0, target: 0 }
});

/** Today's date (yyyy-MM-dd) in the member's timezone, as the stats endpoint expects. */
const todayLocal = (): string => todayInZone(getLocalTimeZone()).toString();

/**
 * Holds the Meals stats. Today's stats (protein ring, macro bars, insight text)
 * live here now; the Weekly Wrap and Progress stats will be incorporated into
 * this cubit as those sections are added.
 */
export default class MealsStatsCubit extends Cubit<MealsStatsState> {
  static keepAlive = true;

  constructor() {
    super(buildInitialState());

    window.addEventListener(globalEvents.USER_CLEAR, () => {
      this.emit(buildInitialState());
    });
  }

  /**
   * Marks today's stats stale so the next mount refetches them. Used after a
   * meal is saved/discarded/deleted: the member returns to the Meals tab and
   * the ring and macro bars reload with the updated totals instead of the
   * cached ones.
   */
  readonly invalidate = (): void => {
    this.patch({ loaded: false });
  };

  readonly load = async (): Promise<void> => {
    if (this.state.loading) {
      return;
    }

    this.patch({ loading: true, error: undefined });

    try {
      const response = await MealInsightControllerService.getMealInsightStats(
        "day",
        todayLocal()
      );
      const stats = response.data;

      // Macro `target` and calorie `floor` are absent when the member's profile
      // lacks the inputs to compute a personalized goal; fall back to 0 so the
      // rings/bars render empty rather than blowing up.
      this.patch({
        insights: stats.insights ?? "",
        protein: {
          total: stats.protein.actual,
          floor: stats.protein.target ?? 0
        },
        fiber: { total: stats.fiber.actual, floor: stats.fiber.target ?? 0 },
        calories: {
          total: stats.calories.total,
          target: stats.calories.floor ?? 0
        },
        loaded: true
      });
    } catch (e) {
      reportErrorSentry(e);
      this.patch({ error: e as Error });
    } finally {
      this.patch({ loading: false });
    }
  };
}
